import { Injectable, Injector } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatDialog } from "@angular/material/dialog";
import { Messaging, getToken, onMessage, isSupported } from "@angular/fire/messaging";
import { BehaviorSubject } from "rxjs";
import { NotificationPermissionDialogComponent } from "../shared/components/notification-permission-dialog/notification-permission-dialog.component";
import { environment } from "../../environments/environment";

@Injectable()
export class MessagingService {
  currentMessage = new BehaviorSubject(null);
  private vapidKey = environment.vapidKey;
  private messaging: Messaging | null = null;
  private permissionDeniedShown = false; // Track if we've already shown the message

  constructor(
    private injector: Injector,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  async initialize() {
    const supported = await isSupported();
    if (supported) {
      this.messaging = this.injector.get(Messaging);
    } else {
      console.warn("Firebase Messaging is not supported in this browser.");
    }
  }

  /**
   * Request notification permission (legacy method - token is now handled by the effect)
   * This method requests permission but doesn't throw errors if permission is denied
   */
  async requestPermission(): Promise<void> {
    // Initialize messaging if not already initialized
    if (!this.messaging) {
      await this.initialize();
      if (!this.messaging) {
        console.warn("Messaging service is not available.");
        return;
      }
    }

    try {
      // Check current permission status
      const permission = await this.getNotificationPermission();

      // If already granted, nothing to do (token will be requested by the effect)
      if (permission === "granted") {
        return;
      }

      // If denied/blocked, show dialog with instructions
      if (permission === "denied") {
        console.warn("Notification permission is denied. User needs to enable it in browser settings.");
        await this.showNotificationPermissionDialog();
        return;
      }

      // Permission is "default", request it
      const newPermission = await this.requestNotificationPermission();
      if (newPermission === "granted") {
        this.permissionDeniedShown = false; // Reset flag if permission is granted
      } else if (newPermission === "denied") {
        // User denied the permission, show dialog with instructions
        await this.showNotificationPermissionDialog();
        console.warn(`Notification permission was not granted. Status: ${newPermission}`);
      } else {
        console.warn(`Notification permission was not granted. Status: ${newPermission}`);
      }
    } catch (error) {
      // Silently handle errors - permission issues are user choices
      console.warn("Error requesting notification permission:", error);
    }
  }

  receiveMessage() {
    if (!this.messaging) {
      console.warn("Messaging service is not available.");
      return;
    }

    onMessage(this.messaging, (payload) => {
      this.currentMessage.next(JSON.stringify(payload));
    });
  }

  /**
   * Get browser-specific information for enabling notifications
   */
  private getBrowserInfo(): { name: string; instructions: string[] } {
    const userAgent = navigator.userAgent.toLowerCase();
    let browserName = "your browser";
    let instructions: string[] = [];

    if (userAgent.includes("chrome") && !userAgent.includes("edg")) {
      browserName = "Chrome";
      instructions = [
        "Click the lock icon (🔒) in the address bar",
        "Select 'Site settings'",
        "Find 'Notifications' and change it to 'Allow'",
        "Refresh the page"
      ];
    } else if (userAgent.includes("firefox")) {
      browserName = "Firefox";
      instructions = [
        "Click the lock icon (🔒) in the address bar",
        "Click 'More Information'",
        "Go to the 'Permissions' tab",
        "Find 'Notifications' and change it to 'Allow'",
        "Refresh the page"
      ];
    } else if (userAgent.includes("safari") && !userAgent.includes("chrome")) {
      browserName = "Safari";
      instructions = [
        "Go to Safari menu → Preferences",
        "Click on 'Websites' tab",
        "Select 'Notifications' from the left sidebar",
        "Find this website and set it to 'Allow'",
        "Refresh the page"
      ];
    } else if (userAgent.includes("edg")) {
      browserName = "Edge";
      instructions = [
        "Click the lock icon (🔒) in the address bar",
        "Select 'Permissions'",
        "Find 'Notifications' and change it to 'Allow'",
        "Refresh the page"
      ];
    } else {
      browserName = "your browser";
      instructions = [
        "Open browser settings",
        "Navigate to Site permissions or Privacy settings",
        "Find 'Notifications' settings",
        "Allow notifications for this website",
        "Refresh the page"
      ];
    }

    return { name: browserName, instructions };
  }

  /**
   * Show a dialog with instructions to enable notifications
   */
  private async showNotificationPermissionDialog(): Promise<boolean> {
    // Only show once per session to avoid annoying the user
    if (this.permissionDeniedShown) {
      return false;
    }

    this.permissionDeniedShown = true;
    const browserInfo = this.getBrowserInfo();

    const dialogRef = this.dialog.open(NotificationPermissionDialogComponent, {
      width: "500px",
      data: {
        browserName: browserInfo.name,
        instructions: browserInfo.instructions,
      },
      disableClose: false,
    });

    return dialogRef.afterClosed().toPromise().then(async (result) => {
      if (result === true) {
        // User clicked "Try Again", check current permission status
        const currentPermission = await this.getNotificationPermission();
        
        if (currentPermission === "granted") {
          // Permission was enabled in browser settings
          this.permissionDeniedShown = false;
          this.snackBar.open("Notifications enabled successfully!", "", {
            duration: 3000,
            panelClass: ["success-snackbar"],
          });
          return true;
        } else if (currentPermission === "default") {
          // Permission was reset, try requesting again
          this.permissionDeniedShown = false;
          const newPermission = await this.requestNotificationPermission();
          if (newPermission === "granted") {
            this.snackBar.open("Notifications enabled successfully!", "", {
              duration: 3000,
              panelClass: ["success-snackbar"],
            });
            return true;
          } else {
            // Still denied, show dialog again
            return this.showNotificationPermissionDialog();
          }
        } else {
          // Still denied, show dialog again with a message
          this.permissionDeniedShown = false;
          this.snackBar.open("Please enable notifications in your browser settings first, then click 'Try Again'.", "", {
            duration: 5000,
            panelClass: ["info-snackbar"],
          });
          return this.showNotificationPermissionDialog();
        }
      }
      return false;
    });
  }

  /**
   * Check the current notification permission status
   */
  async getNotificationPermission(): Promise<NotificationPermission> {
    if (!("Notification" in window)) {
      return "denied";
    }
    return Notification.permission;
  }

  /**
   * Request notification permission from the user
   */
  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!("Notification" in window)) {
      return "denied";
    }

    if (Notification.permission === "granted") {
      return "granted";
    }

    if (Notification.permission === "denied") {
      return Notification.permission;
    }

    // Permission is "default", request it
    try {
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return "denied";
    }
  }

  /**
   * Request Firebase token, ensuring permission is granted first
   */
  async requestToken(): Promise<string> {
    if (!this.messaging) {
      console.warn("Messaging service is not available.");
      return Promise.reject("Messaging service is not available.");
    }

    // Check permission status
    const permission = await this.getNotificationPermission();

    // If permission is denied, reject with a specific error
    // Note: "blocked" is a Firebase error code, but Notification.permission only returns "denied"
    if (permission === "denied") {
      const error = new Error(
        "Notification permission was denied. Please enable it in your browser settings."
      );
      (error as any).code = "messaging/permission-blocked";
      return Promise.reject(error);
    }

    // If permission is default, request it first
    if (permission === "default") {
      const newPermission = await this.requestNotificationPermission();
      if (newPermission !== "granted") {
        const error = new Error("Notification permission was not granted.");
        (error as any).code = `messaging/permission-${newPermission}`;
        return Promise.reject(error);
      }
    }

    // Permission is granted, get the token
    return getToken(this.messaging, { vapidKey: this.vapidKey });
  }
}
